<script setup>
import _map from "lodash/map";
import _cloneDeep from "lodash/cloneDeep";
import Step from "@Components/Stepper/Step.vue";
import { useI18n } from "vue-i18n";
import { FormCard, FormInput, FormListbox, FormSwitch } from "@Components/Form";
import { computed, onMounted } from "vue";
import { fakerFR as faker } from "@faker-js/faker";
import { ArrowPathIcon } from "@heroicons/vue/20/solid";

const props = defineProps(["modelValue", "users", "userRoles", "step"]);
const emit = defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();

const formattedUsers = computed(() => {
    let users = _cloneDeep(props.users);
    return users.map((u) => {
        return {
            id: u.ID,
            name: u.data.display_name,
        };
    });
});

const setFakeUsername = () => {
    let parts = [];

    if (props.modelValue.first_name.length > 0) {
        parts.push(props.modelValue.first_name);
    }

    if (props.modelValue.last_name.length > 0) {
        parts.push(props.modelValue.last_name);
    }

    if (parts.length > 0) {
        parts.push(faker.number.int({ min: 10, max: 99 }));
        props.modelValue.username = parts.join(".");
    } else {
        props.modelValue.username = faker.internet.userName();
    }
};

const setFakePassword = () => {
    props.modelValue.password = faker.internet.password({ length: 16 });
};

onMounted(() => {
    if (props.modelValue.username.length <= 0) {
        setFakeUsername();
    }
    if (props.modelValue.password.length <= 0) {
        setFakePassword();
    }
});
</script>

<template>
    <Step :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ __("Générez un profil IA") }}
                </h3>
            </div>
        </template>
        <div class="mx-auto max-w-4xl flex flex-col space-y-6">
            <FormCard v-if="formattedUsers.length > 0">
                <FormSwitch :modelValue="modelValue.mode === 'create'" @update:modelValue="($event) => (modelValue.mode = $event ? 'create' : 'attach')">{{
                    __("Créer un nouvel utilisateur WordPress")
                }}</FormSwitch>
                <div v-if="modelValue.mode === 'create'" class="grid gap-6 sm:grid-cols-8">
                    <div class="sm:col-span-4">
                        <FormInput
                            :label="__('Nom d\'utilisateur')"
                            v-model="modelValue.username"
                            required
                            :errors="modelValue.errors.get('username')"
                            class="pr-8">
                            <template #action>
                                <div class="absolute inset-y-0 right-0 flex items-center mr-2 select-none">
                                    <ArrowPathIcon class="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" @click="setFakeUsername" />
                                </div>
                            </template>
                        </FormInput>
                    </div>

                    <div class="sm:col-span-4">
                        <FormInput :label="__('Mot de passe')" v-model="modelValue.password" required :errors="modelValue.errors.get('password')" class="pr-8">
                            <template #action>
                                <div class="absolute inset-y-0 right-0 flex items-center mr-2 select-none">
                                    <ArrowPathIcon class="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" @click="setFakePassword" />
                                </div>
                            </template>
                        </FormInput>
                    </div>

                    <div class="sm:col-span-4">
                        <FormInput :label="__('E-mail')" v-model="modelValue.email" :errors="modelValue.errors.get('email')" />
                    </div>

                    <div class="sm:col-span-4">
                        <FormListbox
                            :label="__('Rôle')"
                            v-model="modelValue.role"
                            :items="_map(userRoles, (value, key) => ({ name: value, key: key }))"
                            item-key="key"
                            item-display="name"
                            required
                            :errors="modelValue.errors.get('role')" />
                    </div>
                </div>
                <FormListbox
                    v-else
                    :label="__('Utilisateur')"
                    v-model="modelValue.user_id"
                    :items="formattedUsers"
                    item-key="id"
                    item-display="name"
                    required
                    :errors="modelValue.errors.get('user_id')" />
            </FormCard>
        </div>
    </Step>
</template>
